import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiToken, ScopeCatalogEntry, TokenScope } from '@sss/sdk';
import { sdk } from '../../lib/sdk';

/** Personal Access Tokens — for scripts and local MCP servers (SSS_API_KEY). */
export default function ApiTokensTab() {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [catalog, setCatalog] = useState<ScopeCatalogEntry[]>([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<TokenScope[]>([]);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState<{ id: string; scopes: TokenScope[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => sdk.tokens.list().then(setTokens);

  useEffect(() => {
    let cancelled = false;
    Promise.all([sdk.tokens.list(), sdk.config.get()]).then(
      ([list, config]) => {
        if (cancelled) return;
        setTokens(list);
        setCatalog(config.tokenScopes);
        // Read-only scopes ticked by default.
        setScopes(config.tokenScopes.filter((s) => s.name.endsWith(':read')).map((s) => s.name));
      },
      (err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (list: TokenScope[], scope: TokenScope) => (list.includes(scope) ? list.filter((s) => s !== scope) : [...list, scope]);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const result = await sdk.tokens.create({ name, scopes });
      setCreated(result.key);
      setCopied(false);
      setName('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  async function onSaveScopes() {
    if (!editing) return;
    setError(null);
    try {
      await sdk.tokens.updateScopes(editing.id, editing.scopes);
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  async function onRevoke(id: string) {
    if (!window.confirm(t('settings.apiTokens.revokeConfirm'))) return;
    setError(null);
    try {
      await sdk.tokens.revoke(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  const scopeChecks = (selected: TokenScope[], onToggle: (s: TokenScope) => void) =>
    catalog.map((entry) => (
      <label key={entry.name} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
        <input type="checkbox" checked={selected.includes(entry.name)} onChange={() => onToggle(entry.name)} />
        <span>
          <code>{entry.name}</code> — {entry.description}
        </span>
      </label>
    ));

  if (!tokens && !error) return <p>{t('common.loading')}</p>;

  return (
    <section style={col}>
      <h3 style={{ margin: 0 }}>{t('settings.apiTokens.title')}</h3>
      <p style={{ color: '#666', margin: 0 }}>{t('settings.apiTokens.description')}</p>

      {created && (
        <div style={reveal}>
          <p style={{ margin: 0 }}>{t('settings.apiTokens.created')}</p>
          <code style={{ wordBreak: 'break-all' }}>{created}</code>
          <div>
            <button type="button" onClick={() => navigator.clipboard.writeText(created).then(() => setCopied(true))}>
              {copied ? t('settings.apiTokens.copied') : t('settings.apiTokens.copy')}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onCreate} style={col}>
        <label style={col}>
          {t('settings.apiTokens.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('settings.apiTokens.namePlaceholder')} required maxLength={100} />
        </label>
        <fieldset style={{ ...col, border: '1px solid #ddd', borderRadius: 4 }}>
          <legend>{t('settings.apiTokens.scopes')}</legend>
          {scopeChecks(scopes, (s) => setScopes((prev) => toggle(prev, s)))}
        </fieldset>
        <div>
          <button type="submit" disabled={!name.trim() || scopes.length === 0}>
            {t('settings.apiTokens.create')}
          </button>
        </div>
      </form>

      {tokens?.length === 0 && <p style={{ color: '#666' }}>{t('settings.apiTokens.none')}</p>}
      <ul style={list}>
        {tokens?.map((token) => (
          <li key={token.id} style={row}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <strong>{token.name}</strong> <code style={{ color: '#666' }}>sss_pat_{token.keyPrefix}_…</code>
                <div style={{ color: '#666', fontSize: '0.85rem' }}>
                  {token.scopes.join(', ')} ·{' '}
                  {token.lastUsed ? t('settings.apiTokens.lastUsed', { date: new Date(token.lastUsed).toLocaleDateString() }) : t('settings.apiTokens.neverUsed')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <button type="button" onClick={() => setEditing({ id: token.id, scopes: token.scopes })}>
                  {t('settings.apiTokens.editScopes')}
                </button>
                <button type="button" onClick={() => onRevoke(token.id)}>
                  {t('settings.apiTokens.revoke')}
                </button>
              </div>
            </div>
            {editing?.id === token.id && (
              <div style={{ ...col, marginTop: '0.75rem' }}>
                {scopeChecks(editing.scopes, (s) => setEditing({ ...editing, scopes: toggle(editing.scopes, s) }))}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={onSaveScopes} disabled={editing.scopes.length === 0}>
                    {t('settings.apiTokens.saveScopes')}
                  </button>
                  <button type="button" onClick={() => setEditing(null)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {error && <p style={{ color: 'red' }}>{error}</p>}
    </section>
  );
}

const col: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.5rem' };
const list: React.CSSProperties = { listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' };
const row: React.CSSProperties = { padding: '0.75rem', border: '1px solid #ddd', borderRadius: 4 };
const reveal: React.CSSProperties = { ...col, padding: '0.75rem', border: '1px solid #8bc34a', background: '#f3fbe9', borderRadius: 4 };
